import { type FC, useCallback, useState } from "react"

import type { BaseCommunityFragment } from "../../generated/graphql"
import { useCommunityCustomization } from "../../utils/useCommunityCustomization"
import { MaskedIcon } from "../icons/MaskedIcon"
import type { JoinRequirementFieldConfig } from "../JoinRequirementsFields"
import { JoinRequirementsFields } from "../JoinRequirementsFields"
import { ModalBase } from "../ModalBase"
import UbiqButton, { UbiqButtonSize, UbiqButtonType } from "../UbiqButton"

const REQUIREMENTS_MODAL_PANEL_STYLES =
  "w-[calc(100vw-32px)] pt-12 px-6 pb-10 font-mono sm:w-[480px] sm:px-[54px] flex flex-col items-center"

interface RequirementsProps {
  communityToJoin: BaseCommunityFragment | null
  joinRequirementsConfig: { fields: JoinRequirementFieldConfig[] } | null
  isSubmitting: boolean
  error: string | null
  modalViewportStyles: string
  modalContainerStyles: string
  modalPanelStyles: string
  onSubmit: (responses: Record<string, unknown>) => Promise<void>
}

export const Requirements: FC<RequirementsProps> = ({
  communityToJoin,
  joinRequirementsConfig,
  isSubmitting,
  error,
  modalViewportStyles,
  modalContainerStyles,
  modalPanelStyles,
  onSubmit,
}) => {
  const customizationData = useCommunityCustomization()
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [errors, setErrors] = useState<Record<string, string>>({})
  const communityName = communityToJoin?.name ?? customizationData?.name ?? ""
  const logoSrc = customizationData?.styles.global.logo.src || communityToJoin?.iconUrl

  const handleSubmit = useCallback(async () => {
    if (!joinRequirementsConfig) return

    const validationErrors: Record<string, string> = {}
    for (const field of joinRequirementsConfig.fields) {
      if (field.required) {
        const val = values[field.id]
        if (field.type === "checkbox_url" || field.type === "checkbox_text") {
          if (!val) validationErrors[field.id] = "This field is required"
        } else {
          if (!val || (typeof val === "string" && !val.trim())) {
            validationErrors[field.id] = "This field is required"
          }
        }
      }
      if (field.type === "email" && values[field.id]) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(values[field.id] as string)) {
          validationErrors[field.id] = "Invalid email address"
        }
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      return
    }

    setErrors({})
    await onSubmit(values)
  }, [joinRequirementsConfig, values, onSubmit])

  if (!customizationData) return null
  const modalWindowStyles = customizationData.styles.global.modalWindow

  return (
    <ModalBase
      dialogContainerStyles={modalContainerStyles}
      dialogPanelStyles={`${REQUIREMENTS_MODAL_PANEL_STYLES} ${modalWindowStyles.container} ${modalPanelStyles}`}
      dialogViewportStyles={modalViewportStyles}
      show
    >
      {logoSrc && <MaskedIcon additionalStyle="mx-auto mb-6 block" src={logoSrc} width={96} />}
      <h2 className={`mb-4 text-center text-primary-h2 ${modalWindowStyles.title}`}>
        {communityName}
      </h2>
      <p className={`mb-8 text-center text-body-lg text-textPrimary ${modalWindowStyles.subtitle}`}>
        Before joining this community you need to complete the step(s) below.
      </p>

      {joinRequirementsConfig && (
        <JoinRequirementsFields
          className="w-full"
          errors={errors}
          fields={joinRequirementsConfig.fields}
          onChange={(fieldId, value) => {
            setValues((prev) => ({ ...prev, [fieldId]: value }))
            setErrors((prev) => {
              const next = { ...prev }
              delete next[fieldId]
              return next
            })
          }}
          values={values}
        />
      )}

      {error && <p className="mt-4 text-center text-body-sm text-error">{error}</p>}

      <div className="mt-6 w-full flex flex-col items-center">
        <UbiqButton
          buttonType={UbiqButtonType.LIGHT}
          disabled={isSubmitting}
          onClick={() => void handleSubmit()}
          size={UbiqButtonSize.MEDIUM}
          styles="mx-auto !w-fit"
          title={
            isSubmitting ? "LOADING..." : communityToJoin?.requestToJoin ? "APPLY NOW" : "JOIN NOW"
          }
          type="button"
        />
      </div>
    </ModalBase>
  )
}
